import colorLib from '@kurkle/color';
import { DateTime } from 'luxon';
import 'chartjs-adapter-luxon';
import { valueOrDefault } from 'chart.js/helpers';
import { EXTERNAL_ID_PREFIX } from '../enum/constants';
import QRCodeStyling from 'qr-code-styling';
// @ts-nocheck
// Adapted from http://indiegamr.com/generate-repeatable-random-numbers-in-js/
let _seed = Date.now();

export function srand(seed: number) {
  _seed = seed;
}

export function rand(min: number, max: number) {
  min = valueOrDefault(min, 0);
  max = valueOrDefault(max, 0);
  _seed = (_seed * 9301 + 49297) % 233280;
  return min + (_seed / 233280) * (max - min);
}

export function numbers(config: any) {
  const cfg = config || {};
  const min = valueOrDefault(cfg.min, 0);
  const max = valueOrDefault(cfg.max, 100);
  const from = valueOrDefault(cfg.from, []);
  const count = valueOrDefault(cfg.count, 8);
  const decimals = valueOrDefault(cfg.decimals, 8);
  const continuity = valueOrDefault(cfg.continuity, 1);
  const dfactor = Math.pow(10, decimals) || 0;
  const data = [];
  let i, value;

  for (i = 0; i < count; ++i) {
    // @ts-ignore
    value = (from[i] || 0) + this.rand(min, max);
    // @ts-ignore
    if (this.rand() <= continuity) {
      data.push(Math.round(dfactor * value) / dfactor);
    } else {
      data.push(null);
    }
  }

  return data;
}

export function points(config: any) {
  // @ts-ignore
  const xs = this.numbers(config);
  // @ts-ignore
  const ys = this.numbers(config);
  return xs.map((x: any, i: any) => ({ x, y: ys[i] }));
}

export function bubbles(config: any) {
  // @ts-ignore
  return this.points(config).map(pt => {
    // @ts-ignore
    pt.r = this.rand(config.rmin, config.rmax);
    return pt;
  });
}

export function labels(config: any) {
  const cfg = config || {};
  const min = cfg.min || 0;
  const max = cfg.max || 100;
  const count = cfg.count || 8;
  const step = (max - min) / count;
  const decimals = cfg.decimals || 8;
  const dfactor = Math.pow(10, decimals) || 0;
  const prefix = cfg.prefix || '';
  const values = [];
  let i;

  for (i = min; i < max; i += step) {
    values.push(prefix + Math.round(dfactor * i) / dfactor);
  }

  return values;
}

const MONTHS = [
  'Січень',
  'Лютий',
  'Березень',
  'Квітень',
  'Травень',
  'Червень',
  'Липень',
  'Серпень',
  'Вересень',
  'Жовтень',
  'Листопад',
  'Грудень',
];

export function getMonths(config: any) {
  const cfg = config || {};
  const count = cfg.count || 12;
  const section = cfg.section;
  const values = [];
  let i, value;

  for (i = 0; i < count; ++i) {
    value = MONTHS[Math.ceil(i) % 12];
    values.push(value.substring(0, section));
  }

  return values;
}

const COLORS = [
  '#4dc9f6',
  '#f67019',
  '#f53794',
  '#537bc4',
  '#acc236',
  '#166a8f',
  '#00a950',
  '#58595b',
  '#8549ba',
];

export function color(index: number) {
  return COLORS[index % COLORS.length];
}

export function transparentize(value: string, opacity?: number) {
  const alpha = opacity === undefined ? 0.5 : 1 - opacity;
  return colorLib(value).alpha(alpha).rgbString();
}

export const CHART_COLORS = {
  red: 'rgb(255, 99, 132)',
  orange: 'rgb(255, 159, 64)',
  yellow: 'rgb(255, 205, 86)',
  green: 'rgb(75, 192, 192)',
  blue: 'rgb(54, 162, 235)',
  purple: 'rgb(153, 102, 255)',
  grey: 'rgb(201, 203, 207)',
};

const NAMED_COLORS = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.yellow,
  CHART_COLORS.green,
  CHART_COLORS.blue,
  CHART_COLORS.purple,
  CHART_COLORS.grey,
];

export function namedColor(index: number) {
  return NAMED_COLORS[index % NAMED_COLORS.length];
}

export function newDate(days: number) {
  return DateTime.now().plus({ days }).toJSDate();
}

export function newDateString(days: number) {
  return DateTime.now().plus({ days }).toISO();
}

export function parseISODate(str: string) {
  return DateTime.fromISO(str);
}

export function getExternalIDPrefixType(id?: string): string | null {
  const prefixes = Object.values(EXTERNAL_ID_PREFIX)
    .sort((a, b) => b.length - a.length);

  for (const prefix of prefixes) {
    if (id?.startsWith(prefix)) {
      return prefix;
    }
  }
  return null;
}

export function getIntegrationQrCode(qrData: string, qrImagePath: string): QRCodeStyling {
  return new QRCodeStyling({
    width: 280,
    height: 280,
    margin: 0,
    data: qrData,
    image: qrImagePath,
    type: 'svg',
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: 0.4,
      margin: 5,
    },
    backgroundOptions: {
      round: 0,
      color: '#ffffff',
    },
    dotsOptions: {
      type: 'rounded',
      color: '#000000',
      roundSize: true,
    },
    cornersSquareOptions: {
      type: 'extra-rounded',
      color: '#000000',
    },
    cornersDotOptions: {
      color: '#000000',
    },
    qrOptions: {
      errorCorrectionLevel: 'M',
    },
  });
}

export const isLgMobile = (): boolean => {
  return window.innerWidth < 1200;
};

export const todayDate = (): Date => {
  return new Date();
};

export const formatTimeTo24H = (date: Date): string => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

export const isSameLocalDate = (d1: Date, d2: Date): boolean =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

export const getMinTime = (date: string | null): string | null => {
  if (!date) return null;

  const today = todayDate();
  const input = new Date(date);

  if (isSameLocalDate(today, input)) {
    return formatTimeTo24H(today);
  }

  return null;
};

export const isEllipsisActive = (e: HTMLElement): boolean => {
  return e ? (e.clientWidth < e.scrollWidth) : false;
};

export const capitalizeFirstLetter = (input: string) => {
  if (!input) return input;

  const lower = input.toLowerCase().trim();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};


export function normalizeKw(kw: string): string {
  return (kw || '').trim().replace(/\s+/g, ' ');
}

export function normalizeList(list: string[] = []): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of list) {
    const n = normalizeKw(raw);
    if (!n) continue;
    const key = n.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(n);
    }
  }
  return out;
}
